import { Button, Flex } from 'antd';
import { FC, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
  Spinner,
  TextEllipsis,
} from '../../common/components';
import { keepStaleInMinutes } from '../../common/queryClient';
import { getMapBy } from '../../common/utils';
import { AlgorithmCollapsibleHeader } from '../algorithms/AlgorithmCollapsibleHeader';
import { RenderRow } from '../algorithms/AlgorithmInfo';
import { useGetAlgorithmJoinsQuery } from '../algorithms/joins/query/useGetAlgorithmJoinsQuery';
import { useGetAlgorithmMappingsQuery } from '../algorithms/mappings/query/useGetAlgorithmMappingsQuery';
import { useListAlgorithmsByIdsQuery } from '../algorithms/query/useGetAlgorithmsByIdsQuery';
import { AlgorithmExtendedType } from '../algorithms/schema/types';
import { useGetAlgorithmSourcesQuery } from '../algorithms/sources/query/useGetAlgorithmSourcesQuery';
import { AlgorithmSourceFormDataType } from '../algorithms/sources/schema/types';
import { useGetAlgorithmTargetsQuery } from '../algorithms/targets/query/useGetAlgorithmTargetsQuery';
import { AlgorithmTargetFormDataType } from '../algorithms/targets/schema/types';
import {
  AlgorithmMapping,
  findAlgorithmAlternative,
  initAlgorithmAlternatives,
} from '../algorithms/utils';
import { DataObjectDescriptionVersionStore } from '../DataObjectDescriptionVersionStore';
import { RequiredKeyDataType } from '../schema';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './RelatedAlgorithmsViewModal.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'related-algorithms-view-modal',
);
addTranslations(translations);

export interface RelatedAlgorithmsViewPanelProps {
  onClose: () => void;
  requiredKey: RequiredKeyDataType;
  isEditMode: boolean;
}

export const RelatedAlgorithmsViewModal: FC<RelatedAlgorithmsViewPanelProps> = (
  props,
) => {
  const { onClose, requiredKey, isEditMode } = props;
  const { data: algorithms, isLoading } = useListAlgorithmsByIdsQuery({
    algorithmIds: requiredKey.relatedAlgorithmIds,
  });

  // TODO: show spinner
  if (isLoading || !algorithms) {
    return null;
  }

  return (
    <RelatedAlgorithmsViewModalInner
      algorithms={algorithms}
      onClose={onClose}
      requiredKey={requiredKey}
      isEditMode={isEditMode}
    />
  );
};

interface RelatedAlgorithmsViewModalInnerProps {
  onClose: () => void;
  requiredKey: RequiredKeyDataType;
  algorithms: AlgorithmExtendedType[];
  isEditMode: boolean;
}

const RelatedAlgorithmsViewModalInner: FC<
  RelatedAlgorithmsViewModalInnerProps
> = (props) => {
  const { algorithms, onClose, requiredKey, isEditMode } = props;
  const { t } = useTranslation();

  const sourcesById = useMemo(
    () => getMapBy(algorithms.flatMap((a) => a.sources).filter(Boolean), 'id'),
    [algorithms],
  );
  const mappingsById = useMemo(
    () => getMapBy(algorithms.flatMap((a) => a.mappings).filter(Boolean), 'id'),
    [algorithms],
  );

  return (
    <Modal
      onCancel={() => onClose()}
      width="50rem"
      footer={
        <ModalFooter>
          <Button
            id="close-related-algorithms-modal"
            type="primary"
            onClick={onClose}
          >
            {t('button.close')}
          </Button>
        </ModalFooter>
      }
    >
      <ModalHeader>
        <StyledModalTitle>
          <TextEllipsis>{t('title', requiredKey)}</TextEllipsis>
        </StyledModalTitle>
      </ModalHeader>
      <Padding scrollable>
        <Flex vertical gap="middle">
          {algorithms?.map((algorithm, index) => (
            <CollapsibleRelatedAlgorithm
              key={index}
              algorithm={algorithm}
              sourcesById={sourcesById}
              isEditMode={isEditMode}
              mappingsById={mappingsById}
              requiredKey={requiredKey}
              defaultExpanded={algorithms.length === 1}
            />
          ))}
        </Flex>
      </Padding>
    </Modal>
  );
};

const StyledModalTitle = styled(ModalTitle)`
  padding-right: 30px;
`;

export interface CollapsibleRelatedAlgorithmProps {
  algorithm: AlgorithmExtendedType;
  sourcesById: Map<number, AlgorithmSourceFormDataType>;
  isEditMode: boolean;
  mappingsById: Map<number, AlgorithmMapping>;
  requiredKey: RequiredKeyDataType;
  defaultExpanded?: boolean;
}

export const CollapsibleRelatedAlgorithm: FC<
  CollapsibleRelatedAlgorithmProps
> = (props) => {
  const {
    algorithm,
    sourcesById,
    isEditMode,
    mappingsById,
    requiredKey,
    defaultExpanded,
  } = props;
  const { t } = useTranslation();
  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');

  const alternative = useMemo(
    () =>
      findAlgorithmAlternative(
        algorithm?.code || '',
        algorithm?.groupCode || '',
        initAlgorithmAlternatives(
          dataObjectDescriptionVersion.alternativeAlgorithmSets ?? [],
        ),
      ),
    [dataObjectDescriptionVersion, algorithm],
  );

  const [isExpanded, setIsExpanded] = useState(defaultExpanded ?? false);

  const queryOptions = getQueryOptions(Boolean(isExpanded));
  const sources = useGetAlgorithmSourcesQuery(algorithm, queryOptions);
  const mappings = useGetAlgorithmMappingsQuery(algorithm, queryOptions);
  const joins = useGetAlgorithmJoinsQuery(algorithm, queryOptions);
  const targets = useGetAlgorithmTargetsQuery(algorithm, queryOptions);

  const algorithmWithRelations: AlgorithmExtendedType = useMemo(
    () =>
      sources.data && mappings.data && joins.data && targets.data
        ? {
            ...algorithm,
            sources: sources.data.sources,
            mappings: mappings.data.mappings,
            joins: joins.data.joins,
            targets: targets.data.targets,
          }
        : algorithm,
    [algorithm, sources.data, mappings.data, joins.data, targets.data],
  );

  const renderRow: RenderRow = useCallback(
    (key, value, defaultNode) => {
      if (key === 'targets') {
        return (
          <strong>
            {t('targets.label', {
              length: `${
                value.filter(
                  ({ targetKey }: AlgorithmTargetFormDataType) =>
                    targetKey === requiredKey.key,
                ).length
              }/${value.length}`,
            })}
            :
          </strong>
        );
      }

      const targetRowMatch = key.match(/^targets\.[0-9]+\.?(.+)?/);
      if (targetRowMatch) {
        const [, targetFieldName] = targetRowMatch;
        if (
          targetFieldName === 'targetKey' ||
          (!targetFieldName &&
            (value as AlgorithmTargetFormDataType).targetKey !==
              requiredKey.key)
        ) {
          return null;
        }
      }

      return defaultNode;
    },
    [requiredKey.key, t],
  );

  return (
    <Wrapper>
      {[sources, mappings, joins, targets].some(
        ({ isLoading }) => isLoading,
      ) && <Spinner overlay />}
      <AlgorithmCollapsibleHeader
        algorithm={algorithmWithRelations}
        sourcesById={sourcesById}
        mappingsById={mappingsById}
        alternativeCode={alternative?.code}
        isExpanded={isExpanded}
        onToggleExpanded={() => setIsExpanded(!isExpanded)}
        isEditMode={isEditMode}
        renderRow={renderRow}
      />
    </Wrapper>
  );
};

const getQueryOptions = (enabled: boolean) => ({
  enabled,
  ...keepStaleInMinutes(5),
});

const Wrapper = styled.div`
  position: relative;
`;
